import React from "react";

const MovieDetailsSkeleton = () => {
  return (
    <section className="bg-black min-h-screen">
      {/* Hero Section Skeleton */}
      <div className="relative w-full h-[auto] min-h-[60vh] md:h-[80vh] overflow-hidden pt-16 md:pt-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray/40 to-gray/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        
        {/* Content Overlay Skeleton */}
        <div className="relative z-10 h-full flex items-center md:items-end padding py-8 md:pb-12">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-end">
              {/* Poster Skeleton */}
              <div className="flex-shrink-0">
                <div className="w-[160px] sm:w-[180px] md:w-[280px] aspect-[2/3] rounded-lg bg-gray/30 backdrop-blur-sm border border-white/10 overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray/40 via-white/10 to-gray/40 animate-shimmer"></div>
                </div>
              </div>

              {/* Movie Info Skeleton */}
              <div className="flex-1 text-white space-y-4">
                {/* Title Skeleton */}
                <div className="h-8 md:h-12 lg:h-16 bg-gray/30 rounded-lg w-3/4 backdrop-blur-sm overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
                </div>
                
                {/* Tagline Skeleton */}
                <div className="h-5 md:h-6 bg-gray/20 rounded w-1/2 backdrop-blur-sm overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                </div>

                {/* Meta Info Skeleton */}
                <div className="flex flex-wrap items-center gap-3 md:gap-4 lg:gap-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-8 bg-gray/30 rounded-lg px-4 backdrop-blur-sm overflow-hidden relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
                    </div>
                  ))}
                </div>

                {/* Genres Skeleton */}
                <div className="flex flex-wrap gap-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-6 bg-gray/20 rounded-full w-20 backdrop-blur-sm overflow-hidden relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                    </div>
                  ))}
                </div>

                {/* Overview Skeleton */}
                <div className="space-y-2 max-w-3xl">
                  <div className="h-4 bg-gray/20 rounded w-full backdrop-blur-sm overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                  </div>
                  <div className="h-4 bg-gray/20 rounded w-full backdrop-blur-sm overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                  </div>
                  <div className="h-4 bg-gray/20 rounded w-3/4 backdrop-blur-sm overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Skeleton */}
      <div className="padding pt-8 space-y-8">
        {/* Overview Section Skeleton */}
        <div className="space-y-3">
          <div className="h-6 md:h-8 bg-gray/30 rounded w-32 backdrop-blur-sm overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
          </div>
          <div className="space-y-2 max-w-4xl">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-4 bg-gray/20 rounded w-full backdrop-blur-sm overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Info Cards Skeleton */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
              <div className="h-5 bg-gray/30 rounded w-24 mb-3 backdrop-blur-sm overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[...Array(2)].map((_, j) => (
                  <div key={j} className="h-6 bg-gray/30 rounded w-16 backdrop-blur-sm overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Trailer Section Skeleton */}
        <div className="space-y-4">
          <div className="h-6 md:h-8 bg-gray/30 rounded w-40 backdrop-blur-sm overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="aspect-video rounded-lg bg-gray/20 border border-white/10 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Cast Section Skeleton */}
        <div className="space-y-4">
          <div className="h-6 md:h-8 bg-gray/30 rounded w-24 backdrop-blur-sm overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
          </div>
          <div className="flex gap-4 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex-shrink-0 flex flex-col items-center space-y-2">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gray/30 backdrop-blur-sm overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray/30 via-white/10 to-gray/30 animate-shimmer"></div>
                </div>
                <div className="h-3 bg-gray/20 rounded w-16 backdrop-blur-sm overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                </div>
                <div className="h-2 bg-gray/20 rounded w-12 backdrop-blur-sm overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-gray/20 via-white/10 to-gray/20 animate-shimmer"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MovieDetailsSkeleton;

